import { Box, Stack, styled } from '@mui/material'
import {
    AiDataPrivacyInfo,
    ClankerCheckImage,
    ClankerFileImage,
    ClankerTransformImage,
    ContentChip,
    IllustrationCard,
    tokens,
} from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { SparkleIcon } from '@phosphor-icons/react'
import { type ComponentType, type SVGProps } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { STATIC_APP_PATHS } from 'ClientPortal/src/router/constants'

type NewsItem = {
    title: string
    text: string
    Image: ComponentType<SVGProps<SVGSVGElement>>
    hoverColor: string
    to?: string
}

const SCROLL_SHADOW_WIDTH = '32px'
const SCROLL_SHADOW_COLOR = 'rgba(0, 0, 0, 0.15)'

export function NewsFromEinwertWidget() {
    const { t: tAiDataPrivacyInfo } = useTranslation('terminology', { keyPrefix: 'common.aiDataPrivacyInfo' })
    const { t: tNewsFromEinwertWidget } = useTranslation('pages', { keyPrefix: 'dashboard.newsFromEinwertWidget' })
    const navigate = useNavigate()
    const newsItems: NewsItem[] = [
        {
            title: tNewsFromEinwertWidget('cards.pdfToValXml.title'),
            text: tNewsFromEinwertWidget('cards.pdfToValXml.text'),
            Image: ClankerFileImage,
            hoverColor: tokens.ColorGraphSequentialSixLightest,
            to: STATIC_APP_PATHS.playgroundValXmlExtractor,
        },
        {
            title: tNewsFromEinwertWidget('cards.autoPlausibility.title'),
            text: tNewsFromEinwertWidget('cards.autoPlausibility.text'),
            Image: ClankerCheckImage,
            hoverColor: tokens.ColorGraphSequentialFiveLightest,
            to: STATIC_APP_PATHS.playgroundPdfValidator,
        },
        {
            title: tNewsFromEinwertWidget('cards.tenantListImport.title'),
            text: tNewsFromEinwertWidget('cards.tenantListImport.text'),
            Image: TenantListImage,
            hoverColor: tokens.ColorGraphSequentialFourLightest,
            to: STATIC_APP_PATHS.playgroundTenantListImport,
        },
    ]

    return (
        <CardContainer>
            <Stack direction="row" justifyContent="space-between" width="100%" alignItems="flex-start" gap={tokens.SizingSpacing150}>
                <Stack direction="column" gap={tokens.SizingSpacing075}>
                    <Headline title={tNewsFromEinwertWidget('title')} variant="headline2" />
                    <IntroText direction="column">
                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                            {tNewsFromEinwertWidget('introLineOne')}
                        </Typography>
                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                            {tNewsFromEinwertWidget('introLineTwo')}
                        </Typography>
                    </IntroText>
                </Stack>
                <AiDataPrivacyInfo
                    translations={{
                        tooltip: tAiDataPrivacyInfo('tooltip'),
                        label: tAiDataPrivacyInfo('label'),
                        modalTitle: tAiDataPrivacyInfo('modalTitle'),
                        closeButtonLabel: tAiDataPrivacyInfo('closeButtonLabel'),
                        items: {
                            confidentialData: tAiDataPrivacyInfo('items.confidentialData'),
                            storageDuration: tAiDataPrivacyInfo('items.storageDuration'),
                            euProcessing: tAiDataPrivacyInfo('items.euProcessing'),
                            noModelTraining: tAiDataPrivacyInfo('items.noModelTraining'),
                            supportPrefix: tAiDataPrivacyInfo('items.supportPrefix'),
                            supportLinkLabel: tAiDataPrivacyInfo('items.supportLinkLabel'),
                        },
                    }}
                />
            </Stack>
            <CardsGrid>
                <LeftScrollShadow data-scroll-shadow="left" aria-hidden="true" />
                <RightScrollShadow data-scroll-shadow="right" aria-hidden="true" />
                {newsItems.map(({ title, text, Image, hoverColor, to }) => (
                    <IllustrationCard
                        key={title}
                        variant="horizontal"
                        title={title}
                        text={text}
                        hoverColor={hoverColor}
                        image={<Image width="100%" height="100%" />}
                        arrowLabel={to ? tNewsFromEinwertWidget('tryLabel') : undefined}
                        contentChip={
                            to ? (
                                <ContentChip
                                    size="small"
                                    color="purple"
                                    label={tNewsFromEinwertWidget('betaLabel')}
                                    icon={<SparkleIcon />}
                                />
                            ) : undefined
                        }
                        hideArrow={!to}
                        disabled={!to}
                        onClick={() => {
                            if (to) {
                                void navigate(to)
                            }
                        }}
                    />
                ))}
            </CardsGrid>
            <DisclaimerText variant="textXS" color={tokens.ColorTextSubtlest}>
                {tNewsFromEinwertWidget('disclaimer')}
            </DisclaimerText>
        </CardContainer>
    )
}

const TenantListImage = styled(ClankerTransformImage)({
    boxSizing: 'border-box',
    width: '175px',
    height: '135px',
    paddingInline: '20px',
    backgroundColor: tokens.ColorGraphSequentialFourLightest,
    borderRadius: tokens.SizingCornerS,
})

const CardContainer = styled(Stack)(({ theme }) => ({
    border: `1px solid ${theme.palette.secondary1.grey}`,
    padding: theme.spacing(4),
    background: theme.palette.white,
    borderRadius: '12px',
    minHeight: '120px',
    width: '100%',
    gap: tokens.SizingSpacing150,
}))

const IntroText = styled(Stack)(() => ({
    maxWidth: '700px',
}))

const CardsGrid = styled(Box)(() => ({
    containerType: 'scroll-state',
    display: 'flex',
    gap: tokens.SizingSpacing300,
    overflowX: 'auto',
    width: '100%',
    paddingBottom: tokens.SizingSpacing050,
    '& > :not([data-scroll-shadow])': {
        flex: '1 0 386px',
    },
}))

const ScrollShadow = styled(Box)(() => ({
    position: 'sticky',
    alignSelf: 'stretch',
    flex: `0 0 ${SCROLL_SHADOW_WIDTH}`,
    marginRight: `calc(-${SCROLL_SHADOW_WIDTH} - ${tokens.SizingSpacing300})`,
    zIndex: 1,
    pointerEvents: 'none',
    opacity: 0,
    transition: 'opacity 150ms ease',
}))

const LeftScrollShadow = styled(ScrollShadow)(() => ({
    left: 0,
    boxShadow: `inset 16px 0 16px -16px ${SCROLL_SHADOW_COLOR}`,
    '@container scroll-state(scrollable: left)': {
        opacity: 1,
    },
}))

const RightScrollShadow = styled(ScrollShadow)(() => ({
    left: `calc(100% - ${SCROLL_SHADOW_WIDTH})`,
    boxShadow: `inset -16px 0 16px -16px ${SCROLL_SHADOW_COLOR}`,
    '@container scroll-state(scrollable: right)': {
        opacity: 1,
    },
}))

const DisclaimerText = styled(Typography)(() => ({
    width: '100%',
    textAlign: 'center',
}))
